export interface MarketDataHistoryLimit {
  reason: "market_data_unavailable";
  validation_code: "market_data_history_limit";
  retryable: false;
  limits: {
    provider: "yfinance";
    interval: string;
    history_days: number;
    earliest_start_date: string;
    latest_end_date: string;
    suggested_intervals: string[];
  };
}

const intervalMinutes: Record<string, number> = {
  "2m": 2, "5m": 5, "15m": 15, "30m": 30, "60m": 60, "1h": 60, "90m": 90, "1d": 1440,
};

function record(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function validUTCDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(value)) return false;
  const time = Date.parse(value);
  return Number.isFinite(time) && new Date(time).toISOString() === value.replace("Z", ".000Z");
}

/** Only documented, bounded metadata can become displayed recovery guidance. */
export function parseMarketDataHistoryLimit(body: unknown): MarketDataHistoryLimit | undefined {
  if (!record(body)) return undefined;
  const detail = record(body.detail) ? body.detail : body;
  if (detail.reason !== "market_data_unavailable" || detail.validation_code !== "market_data_history_limit"
    || detail.retryable !== false || !record(detail.limits)) return undefined;
  const limits = detail.limits;
  const interval = typeof limits.interval === "string" ? limits.interval.trim().toLowerCase() : "";
  const days = limits.history_days;
  const suggestions = limits.suggested_intervals;
  if (limits.provider !== "yfinance" || !Object.prototype.hasOwnProperty.call(intervalMinutes, interval) || interval === "1d"
    || typeof days !== "number" || !Number.isInteger(days) || days < 1 || days > 3660
    || !validUTCDate(limits.earliest_start_date) || !validUTCDate(limits.latest_end_date)
    || limits.earliest_start_date >= limits.latest_end_date
    || !Array.isArray(suggestions) || suggestions.length < 1 || suggestions.length > 2
    || !suggestions.every((item) => typeof item === "string" && ["1h", "1d"].includes(item)
      && intervalMinutes[item] > intervalMinutes[interval])
    || new Set(suggestions).size !== suggestions.length) return undefined;
  return {
    reason: "market_data_unavailable", validation_code: "market_data_history_limit", retryable: false,
    limits: {
      provider: "yfinance", interval, history_days: days,
      earliest_start_date: limits.earliest_start_date, latest_end_date: limits.latest_end_date,
      suggested_intervals: [...suggestions],
    },
  };
}
